import { makeAutoObservable, reaction } from 'mobx'
import type AppStore from '../AppStore'
import { type AxiosResponse, type AxiosInstance } from 'axios'
import { type QuarterResponse } from 'Pages/MapPage/@types'
import {
  CachedClient,
  type LazyAggregates,
  createAxiosInstance,
  setFromJSON
} from 'helpers'
import {
  type JSONObject,
  type BuildingUserData,
  type CustomProjectWMSLayer,
  type ProjectHistoryObject,
  type ProjectPatchData,
  type ProjectUserSettings,
  type WMSConfig,
  type FederalStates,
  type ProjectResponse,
  type FeatureFlags
} from 'stores/@types'
import { Building, type BuildingCollection, MultiBuilding } from './Buildings'
import {
  MultiVegetation,
  Vegetation,
  type VegetationCollection
} from './Vegetations'
import {
  EnergyPlant,
  type EnergyPlantCollection,
  MultiEnergyPlant
} from './EnergyPlants'
// eslint-disable-next-line import/no-unresolved
import { type Point } from 'geojson'
import type FilterStore from 'stores/FilterStore'

export enum UserRole {
  Manager = 'manage',
  Editor = 'edit',
  Viewer = 'view',
  Public = 'public'
}

export enum FeatureType {
  Quarter = 'quarter',
  Building = 'building',
  Project = 'project',
  Vegetation = 'vegetation',
  EnergyPlant = 'energyplant',
  Aggregation = 'aggregration',
  Filter = 'filter'
}

export interface AppFeature {
  reset: () => void
  title: string
  get client(): AxiosInstance
  type: FeatureType
  isEditingSupported: boolean
  get monitorData(): JSONObject | undefined
}

export interface ParentFeature extends AppFeature {
  store: AppStore
  filter: string
  buildings?: MultiBuilding
  vegetations?: MultiVegetation
  energyPlants?: MultiEnergyPlant
}

export interface MultiFeature {
  parent: ParentFeature
  count: number | undefined
  simulation: JSONObject | undefined
  aggregates: LazyAggregates
}

export interface WriteFeature {
  patch: (data, itemsToPatch?: number) => Promise<AxiosResponse>
  formPlaceHolder?: JSONObject | undefined
}

interface DataQualityResponse {
  heat_quality__avg: number
  power_quality__avg: number
}

export class Project implements AppFeature, WriteFeature {
  public readonly type = FeatureType.Project
  public readonly filter = ''

  static userSettingsCacheKey = 'userSettings'
  static quarterCacheKey = 'quarters'

  public readonly cached: CachedClient

  public title: string = 'Projekt'
  public user_role?: UserRole

  public center?: Point

  public buildings: MultiBuilding
  public vegetations: MultiVegetation
  public energyPlants: MultiEnergyPlant

  // the following props should be part of the constructor
  public state!: FederalStates

  public heatsupply!: number
  public powersupply!: number

  public population!: number

  public is_scenario: boolean = false
  public all_simulations_updated: boolean = false

  public readonly isEditingSupported: boolean = false

  constructor(
    public readonly store: AppStore,
    public readonly uuid: string
  ) {
    this.buildings = new MultiBuilding(this)
    this.vegetations = new MultiVegetation(this)
    this.energyPlants = new MultiEnergyPlant(this)

    makeAutoObservable(this, { uuid: false })

    this.cached = new CachedClient(this)
  }

  get client(): AxiosInstance {
    return createAxiosInstance(
      (process.env.REACT_APP_API_URL ?? '') + `/api/v1/project/${this.uuid}/`,
      this.store.userStore.accessToken
    )
  }

  get monitorData(): JSONObject | undefined {
    if (this.buildings.simulation === undefined) return
    return {
      ...this.buildings.simulation,
      uuid: this.uuid,
      title: this.title
    }
  }

  getBuilding(uuid: string): Building {
    return new Building(this, uuid)
  }

  getQuarter(uuid: string): Quarter | undefined {
    return this.quarters.get(uuid)
  }

  getVegetation(uuid: string): Vegetation {
    return new Vegetation(this, uuid)
  }

  getEnergyPlant(uuid: string): EnergyPlant {
    return new EnergyPlant(this, uuid)
  }

  refresh(): void {
    void this.client.get<ProjectResponse>(`/`).then((res) => {
      setFromJSON(this, res.data, ['uuid'])
    })
  }

  async patch(data: ProjectPatchData): Promise<AxiosResponse> {
    const promise = this.client.patch(`/`, data)
    return await promise
  }

  get dataQualityAggregation(): DataQualityResponse | undefined {
    return this.buildings.aggregates.aggregate({
      heat_quality: 'avg',
      power_quality: 'avg'
    }) as DataQualityResponse | undefined
  }

  get dataQualityHeat(): number {
    return this.dataQualityAggregation?.heat_quality__avg ?? 0
  }

  get dataQualityPower(): number {
    return this.dataQualityAggregation?.power_quality__avg ?? 0
  }

  get geothermalWMS(): WMSConfig | undefined {
    return this.store.appConfig?.geothermalWMS[this.state]
  }

  get userSettings(): ProjectUserSettings {
    return (
      this.cached.get<ProjectUserSettings>(
        `/usersettings/`,
        Project.userSettingsCacheKey,
        (data) => {
          return {
            projectHistory: data.project_history ?? data.projectHistory ?? [],
            wmsLayerList: data.wmsLayerList ?? []
          }
        }
      ) ?? {}
    )
  }

  get projectFeatureFlags(): FeatureFlags | undefined {
    return this.cached.get('/flags/')
  }

  async updateProjectUserSettings(
    settings: ProjectUserSettings
  ): Promise<AxiosResponse> {
    const promise = this.client.put(`/usersettings/`, settings)
    void promise
      .then(() => {
        this.cached.reset([Project.userSettingsCacheKey])
      })
      .catch()
    return await promise
  }

  async updateWMSLayerList(
    wmsLayerList: CustomProjectWMSLayer[]
  ): Promise<AxiosResponse> {
    const currentSettings = { ...this.userSettings }
    currentSettings.wmsLayerList = [...wmsLayerList]
    return await this.updateProjectUserSettings(currentSettings)
  }

  get history(): ProjectHistoryObject[] {
    return this.userSettings.projectHistory ?? []
  }

  async updateProjectHistory(
    historyObject: ProjectHistoryObject[]
  ): Promise<AxiosResponse> {
    const currentSettings = { ...this.userSettings }
    currentSettings.projectHistory = historyObject
    return await this.updateProjectUserSettings(currentSettings)
  }

  get quarters(): Map<string, Quarter> {
    return (
      this.cached.get<QuarterResponse[], Map<string, Quarter>>(
        `/quarter/`,
        Project.quarterCacheKey,
        (data) =>
          new Map(
            data.map((feature: QuarterResponse) => {
              const quarter = new Quarter(this, feature.uuid)
              setFromJSON(quarter, feature, ['uuid'])
              return [feature.uuid, quarter]
            })
          ),
        true
      ) ?? new Map()
    )
  }

  resetQuarters(): void {
    this.cached.reset([Project.quarterCacheKey])
  }

  get dataQuality(): number {
    return (this.dataQualityHeat + this.dataQualityPower) / 2
  }

  reset(): void {
    this.buildings.reset()
    this.vegetations.reset()
    this.energyPlants.reset()
  }
}

export class CarrierParameter {
  public co2Emission?: number
  public cost?: number
}

export class Scenario {
  public title?: string
  public year?: number
  public population?: number
  public emission_price?: number
  public heating_days?: number
  public outdoor_temperature?: number
  public carrier_parameters?: object = {}
  public description?: string
  public all_simulations_updated?: boolean

  constructor(readonly uuid: string) {
    makeAutoObservable(this, { uuid: false })
  }
}

export class Aggregation implements AppFeature {
  public readonly type = FeatureType.Aggregation

  public buildings: MultiBuilding
  public store: AppStore
  public readonly isEditingSupported: boolean = false

  constructor(
    private readonly project: Project,
    public uuid: string,
    public properties: JSONObject
  ) {
    this.buildings = new MultiBuilding(this)

    makeAutoObservable(this, { uuid: false })
    this.store = project.store
  }

  reset(): void {
    this.buildings.reset()
  }

  get client(): AxiosInstance {
    return this.project.client
  }

  get filter(): string {
    return `${this.properties.layer_id}=${this.uuid}&`
  }

  get title(): string {
    return `${this.properties.layer_label} ${
      this.properties.name ?? this.properties.oid ?? ''
    }`
  }

  get monitorData(): JSONObject | undefined {
    if (
      this.buildings.simulation === undefined ||
      this.properties === undefined
    )
      return
    return {
      ...this.buildings.simulation,
      ...this.properties
    }
  }
}

export class Quarter implements ParentFeature {
  public readonly type = FeatureType.Quarter

  public buildings: MultiBuilding
  public vegetations: MultiVegetation
  public energyPlants: MultiEnergyPlant

  public title: string = 'Gebiet'
  public description: string = ''
  public color: string = '#555555'
  public tags: string[] = []

  public store: AppStore
  public readonly isEditingSupported: boolean = true

  constructor(
    private readonly project: Project,
    public uuid: string
  ) {
    this.buildings = new MultiBuilding(this)
    this.vegetations = new MultiVegetation(this)
    this.energyPlants = new MultiEnergyPlant(this)

    makeAutoObservable(this, { uuid: false })
    this.store = project.store
  }

  // JS/TS inheritance s*** that's why it's doubled
  // eslint-disable-next-line sonarjs/no-identical-functions
  reset(): void {
    this.buildings.reset()
    this.vegetations.reset()
    this.energyPlants.reset()
  }

  get client(): AxiosInstance {
    return this.project.client
  }

  get monitorData(): JSONObject | undefined {
    if (this.buildings.simulation === undefined) return
    return {
      uuid: this.uuid,
      title: this.title,
      ...this.buildings.simulation
    }
  }

  get filter(): string {
    return `quarter=${this.uuid}&`
  }

  get formPlaceHolder(): JSONObject | undefined {
    return this.buildings.formPlaceHolder
  }

  async patch(data: JSONObject): Promise<AxiosResponse> {
    const promise = this.project.client.patch<QuarterResponse>(
      `quarter/${this.uuid}/`,
      data
    )
    void promise
      .then((response) => {
        this.reset()
        setFromJSON(this, response.data)
      })
      .catch()
    return await promise
  }
}

export class Filter implements ParentFeature {
  public readonly type = FeatureType.Filter
  public buildings: MultiBuilding
  public store: AppStore
  public readonly isEditingSupported: boolean = true

  constructor(private readonly project: Project) {
    this.buildings = new MultiBuilding(this)
    makeAutoObservable(this)
    this.store = project.store

    reaction(
      () => this.filter,
      () => {
        this.buildings.reset()
      }
    )
  }

  reset(): void {
    this.buildings.reset()
  }

  get title(): string {
    return `Filterergebnis für ${this.buildings.count ?? 'mehrere'} Objekte`
  }

  get client(): AxiosInstance {
    return this.project.client
  }

  get monitorData(): JSONObject | undefined {
    if (this.buildings.simulation === undefined) return
    return {
      title: this.title,
      ...this.buildings.simulation
    }
  }

  get filter(): string {
    const filterStore: FilterStore = this.project.store.filterStore
    return filterStore.hasBuildingFiltersSelected()
      ? filterStore.filterParameter + '&'
      : ''
  }

  get formPlaceHolder(): JSONObject | undefined {
    return this.buildings?.formPlaceHolder
  }

  async patch(data: BuildingUserData): Promise<AxiosResponse> {
    return await this.buildings.patch(data)
  }
}

export type SingleFeatures =
  | Project
  | Building
  | Quarter
  | Vegetation
  | EnergyPlant
  | Aggregation
  | Filter
export type MultiFeatures =
  | VegetationCollection
  | EnergyPlantCollection
  | BuildingCollection
export type Features = SingleFeatures | MultiFeatures
